import React, { useMemo, useState, useCallback } from "react";
import { UserTaskTypeConstants } from "finhelper";

import { ReactComponent as CaseIcon } from "#app/assets/task/case.svg?react";
import { ReactComponent as UserIcon } from "#app/assets/task/user.svg?react";
import { ReactComponent as PhoneIcon } from "#app/assets/task/phone.svg?react";
import { ReactComponent as LearnIcon } from "#app/assets/task/learn.svg?react";
import { ReactComponent as UsersIcon } from "#app/assets/icons/users.svg?react";
import { ReactComponent as LetterIcon } from "#app/assets/task/letter.svg?react";

interface TaskTypeInterface {
    id: string | number;
    icon: React.ReactNode;
    text: string;
}

interface HookTypeInterface {
    iconsList: Array<TaskTypeInterface>;
    currentIcon: TaskTypeInterface;
    setIcon: (id: string | number | null | undefined) => void;
}

const iconsList = [
    {
        id: UserTaskTypeConstants.CALLS,
        icon: <PhoneIcon />,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.CALLS),
    },
    {
        id: UserTaskTypeConstants.MEETINGS,
        icon: <UserIcon />,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.MEETINGS),
    },
    {
        id: UserTaskTypeConstants.CONSULTATION,
        icon: <LearnIcon />,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.CONSULTATION),
    },
    {
        id: UserTaskTypeConstants.LETTERS,
        icon: <LetterIcon />,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.LETTERS),
    },
    {
        id: UserTaskTypeConstants.OTHER,
        icon: <CaseIcon />,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.OTHER),
    },
    {
        id: UserTaskTypeConstants.PERSONAL,
        icon: <UsersIcon />,
        text: UserTaskTypeConstants.getTextByType(UserTaskTypeConstants.PERSONAL),
    },
];

/**
 * Хук для выбора иконок
 * @param typeId - ID текущей иконки, берется из UserTaskTypeConstants
 * @returns три мемоизованных хука:
 * iconsList - возвращает список загруженных в хук иконок,
 * currentIcon - возвращает текущую иконку,
 * setIcon - меняет возвращаемую по запросам currentIcon.
 */
export const useTaskTypesIcons = (
    typeId: number | null | undefined = null
): {
    setIcon: (id: string | number | null | undefined) => void;
    currentIcon: TaskTypeInterface;
    iconsList: Array<TaskTypeInterface>;
} => {
    const iconsListMemo = useMemo<Array<TaskTypeInterface>>(
        //@ts-ignore
        () => iconsList,
        []
    );

    const [current, setCurrent] = useState<TaskTypeInterface>(() => {
        const initialIcon = iconsListMemo.find((icon) => icon.id === typeId);
        return initialIcon || iconsListMemo[0];
    });

    const setIcon = useCallback(
        (id: string | number | null | undefined): void => {
            if (!id) throw new Error("Параметр id иконки должен быть задан явно");
            if (id === current.id) return;

            const foundIcon = iconsListMemo.find((icon) => icon.id === id);
            if (foundIcon) setCurrent(foundIcon);
            else throw new Error(`Иконка с id ${id} не найдена`);
        },
        [current.id, iconsListMemo]
    );

    return useMemo(
        () => ({
            iconsList: iconsListMemo,
            currentIcon: current,
            setIcon,
        }),
        [iconsListMemo, current, setIcon]
    );
};
